import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import IconButton from 'Components/DesignSystem/IconButton/IconButton';
import Menu from 'Components/DesignSystem/Menu/Menu';
import { MoreVertical } from 'Components/DesignSystem/Iconography/Icons';
import { CREATE_COMPATIBILITY, VIEW_COMPATIBILITY } from '../constants/CategoryRelevancyConstants';

type ThreeDotMenuProps = {
    onCreateCompatibility: () => void;
    onViewCompatibility: () => void;
    disabled?: boolean;
};

const ThreeDotMenu: React.FC<ThreeDotMenuProps> = ({ onCreateCompatibility, onViewCompatibility, disabled }) => {
    const [open, setOpen] = useState(false);
    const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
    const buttonRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (
                buttonRef.current && !buttonRef.current.contains(e.target as Node) &&
                menuRef.current && !menuRef.current.contains(e.target as Node)
            ) {
                setOpen(false);
            }
        };
        const handleScroll = () => setOpen(false);
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('scroll', handleScroll, true);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('scroll', handleScroll, true);
        };
    }, [open]);

    const handleToggle = () => {
        if (disabled) return;
        if (!open && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setMenuPos({ top: rect.bottom + 4, left: rect.right - 192 });
        }
        setOpen(prev => !prev);
    };

    const handleAction = (action: () => void) => {
        setOpen(false);
        action();
    };

    return (
        <div role="button" data-testid="components-threedotmenu-div-1" className="tw-inline-block" onClick={e => e.stopPropagation()}>
            <IconButton
                ref={buttonRef}
                data-testid="category-relevancy-row-menu-btn"
                aria-label="Row actions"
                size="sm"
                onClick={handleToggle}
                disabled={disabled}
            >
                <MoreVertical size={16} />
            </IconButton>
            {open && ReactDOM.createPortal(
                /* The wrapper owns only geometry (the fixed position from the trigger
                   rect); DS Menu brings the popover chrome (§ 217). */
                <div role="button" data-testid="components-threedotmenu-div-2"
                    ref={menuRef}
                    style={{ top: menuPos.top, left: menuPos.left, position: 'fixed', zIndex: 9999, width: 192 }}
                    onClick={e => e.stopPropagation()}
                >
                    <Menu
                        items={[
                            {
                                id: 'create-compatibility',
                                label: CREATE_COMPATIBILITY,
                                'data-testid': 'components-threedotmenu-button-1',
                                onClick: () => handleAction(onCreateCompatibility),
                            },
                            {
                                id: 'view-compatibility',
                                label: VIEW_COMPATIBILITY,
                                'data-testid': 'components-threedotmenu-button-2',
                                onClick: () => handleAction(onViewCompatibility),
                            },
                        ]}
                    />
                </div>,
                document.body
            )}
        </div>
    );
};

export default ThreeDotMenu;
